// Portal accounts are self-registered (or manager-provisioned) people linked to
// units. They live in the resident experience (dues, tickets, notices) rather
// than the management workspace (properties, units, ledger, team).
const PORTAL_ROLES = new Set(['owner', 'tenant', 'resident']);
export const STAFF_ROLES = new Set(['superadmin', 'manager', 'auditor']);

export interface RoleLikeUser {
  role?: string | null;
  available_modes?: string[];
  has_home_mode?: boolean;
}

export const isPortalRole = (role?: string | null): boolean => PORTAL_ROLES.has(role ?? '');

export const isPortalUser = (user?: RoleLikeUser | null): boolean =>
  Boolean(user && user.role !== 'superadmin' && (user.available_modes?.includes('home') || isPortalRole(user.role)));

export const isSuperuserRole = (user?: RoleLikeUser | null): boolean =>
  Boolean(user && user.role === 'superadmin');

export const isContractorUser = (user?: RoleLikeUser | null): boolean =>
  Boolean(user && (user.available_modes?.includes('contractor') || user.role === 'contractor'));

/** True when the account holds a resident hat: a unit role, a home mode, or
 * a portal platform role. */
export const hasHomeHat = (user?: RoleLikeUser | null): boolean =>
  Boolean(
    user &&
      (user.has_home_mode === true ||
        user.available_modes?.includes('home') ||
        isPortalRole(user.role))
  );

export interface ActiveModeLike {
  appMode?: string | null;
  residentMode?: string | null;
  isResidentScope?: boolean | null;
}

/**
 * Active-mode-aware resident check. A valid active mode wins over the static
 * hat so dual-hat accounts acting as PM are not treated as residents.
 * A mode is only honored when the account actually holds that hat
 * (manage needs a staff hat, home needs a resident hat), so a stale stored
 * mode or an unresolved mock cannot flip a pure portal user into staff.
 * Falls back to isPortalUser() while mode is still resolving.
 */
export const isActingResident = (
  user?: RoleLikeUser | null,
  active?: ActiveModeLike | null,
): boolean => {
  const hasManageHat = Boolean(
    user &&
      (STAFF_ROLES.has(user.role ?? '') || user.available_modes?.includes('manage')),
  );
  const homeHat = hasHomeHat(user);
  const wantsManage = active?.residentMode === 'manage' || active?.appMode === 'manage';
  // A stale appMode the user no longer holds must not win. HomePage validates
  // appMode against available_modes the same way: only a held mode counts.
  const manageValid = Boolean(
    hasManageHat &&
      (active?.residentMode === 'manage' ||
        (active?.appMode === 'manage' &&
          (!user?.available_modes || user.available_modes.includes('manage')))),
  );
  if (wantsManage && manageValid) return false;
  if (active?.isResidentScope) return true;
  const wantsHome = active?.residentMode === 'home' || active?.appMode === 'home';
  if (wantsHome && (homeHat || !user)) return true;
  if (wantsHome && !hasManageHat) return true;
  return isPortalUser(user);
};

/**
 * Mode-aware contractor check. An explicit home/manage selection wins over
 * the static contractor role, so dual contractor+resident accounts acting in
 * My Home are not bounced to the contractor portal. Falls back to
 * isContractorUser() while mode is still resolving.
 */
export const isActingContractor = (
  user?: RoleLikeUser | null,
  active?: ActiveModeLike | null,
): boolean => {
  if (!isContractorUser(user)) return false;
  const modesKnown = Array.isArray(user?.available_modes);
  const modeValid = (m: string): boolean =>
    !modesKnown || Boolean(user?.available_modes?.includes(m));
  if (active?.residentMode === 'home' || (active?.appMode === 'home' && modeValid('home')))
    return false;
  if (active?.residentMode === 'manage' || (active?.appMode === 'manage' && modeValid('manage')))
    return false;
  if (active?.appMode === 'contractor') return modeValid('contractor');
  return true;
};

